import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /**
   * Starting Y position of the current touch gesture.
   * @type {number}
   */
  #touchStartY = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keyup', this.#onKeyUp);
    this.#disableBackgroundScrollGuard();
  }

  /**
   * Close the main menu drawer when Escape is pressed.
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Whether the main menu drawer is open.
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target.
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Save the beginning of a touch gesture.
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    this.#touchStartY = event.touches[0]?.clientY ?? 0;
  };

  /**
   * Prevent a touch gesture from scrolling the page behind the drawer.
   *
   * Scrolling inside the drawer remains enabled. If the drawer reaches
   * its top or bottom edge, the gesture is stopped instead of chaining
   * through to the underlying page.
   *
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    if (!(event.target instanceof Element)) {
      event.preventDefault();
      return;
    }

    const drawer = this.refs.menuDrawer;

    if (!drawer.contains(event.target)) {
      event.preventDefault();
      return;
    }

    const currentY = event.touches[0]?.clientY ?? this.#touchStartY;
    const deltaY = currentY - this.#touchStartY;

    const atTop = drawer.scrollTop <= 0;
    const atBottom =
      drawer.scrollTop + drawer.clientHeight >= drawer.scrollHeight - 1;

    const hasScrollableContent =
      drawer.scrollHeight > drawer.clientHeight;

    if (!hasScrollableContent) {
      event.preventDefault();
      return;
    }

    // Finger moving downward while already at top.
    if (deltaY > 0 && atTop) {
      event.preventDefault();
      return;
    }

    // Finger moving upward while already at bottom.
    if (deltaY < 0 && atBottom) {
      event.preventDefault();
    }
  };

  /**
   * Stop mouse/trackpad wheel scrolling from escaping the drawer.
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    if (!(event.target instanceof Element)) {
      event.preventDefault();
      return;
    }

    const drawer = this.refs.menuDrawer;

    if (!drawer.contains(event.target)) {
      event.preventDefault();
      return;
    }

    const atTop = drawer.scrollTop <= 0;
    const atBottom =
      drawer.scrollTop + drawer.clientHeight >= drawer.scrollHeight - 1;

    if (event.deltaY < 0 && atTop) {
      event.preventDefault();
      return;
    }

    if (event.deltaY > 0 && atBottom) {
      event.preventDefault();
    }
  };

  /**
   * Prevent the page behind the drawer from scrolling.
   */
  #enableBackgroundScrollGuard() {
    document.addEventListener('touchstart', this.#handleTouchStart, {
      passive: true,
    });

    document.addEventListener('touchmove', this.#handleTouchMove, {
      passive: false,
    });

    document.addEventListener('wheel', this.#handleWheel, {
      passive: false,
    });
  }

  /**
   * Restore normal page scrolling.
   */
  #disableBackgroundScrollGuard() {
    document.removeEventListener('touchstart', this.#handleTouchStart);

    document.removeEventListener('touchmove', this.#handleTouchMove);

    document.removeEventListener('wheel', this.#handleWheel);
  }

  /**
   * Toggle the main menu drawer.
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer.
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      this.#enableBackgroundScrollGuard();
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add(
          'menu-drawer--has-submenu-opened'
        );
      }

      const drawer = details.querySelector(
        '.menu-drawer, .menu-drawer__submenu'
      );

      onAnimationEnd(
        drawer || details,
        () => trapFocus(details),
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer.
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer.
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest open menu or submenu.
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const isMainDrawer = details === this.refs.details;

    summary.setAttribute('aria-expanded', 'false');

    details.classList.remove('menu-open');

    this.refs.menuDrawer.classList.remove(
      'menu-drawer--has-submenu-opened'
    );

    const drawer = details.querySelector(
      '.menu-drawer, .menu-drawer__submenu'
    );

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);

        if (isMainDrawer) {
          removeTrapFocus();

          const openDetails = this.querySelectorAll(
            'details[open]:not(accordion-custom > details)'
          );

          openDetails.forEach(reset);

          this.#disableBackgroundScrollGuard();
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend listeners to animated drawer elements.
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll(
      '.menu-drawer__animated-element'
    );

    allAnimated.forEach((element) => {
      element.addEventListener(
        'animationend',
        removeWillChangeOnAnimationEnd
      );
    });
  }

  /**
   * Temporarily disable accordion animations when opening the drawer.
   * @param {HTMLDetailsElement} details
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll(
      'accordion-custom .details-content'
    );

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add(
          'details-content--no-animation'
        );
      }
    });

    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove(
            'details-content--no-animation'
          );
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state.
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');

  element
    .querySelector('summary')
    ?.setAttribute('aria-expanded', 'false');
}